import { Injectable } from '@angular/core';
import currency from 'currency.js';
import { OrderInterface } from '../../_generated/api_interfaces/api/order.interface';
import { LineItemInterface } from '../../_generated/api_interfaces/api/lineItem.interface';

type Order = ReturnType<OrderInterface['apiOrdersIdGet']>['data'];
type LineItem = ReturnType<LineItemInterface['apiLineItemsIdGet']>['data'];

@Injectable({
    providedIn: 'root',
})
export class CartHelpersService {
    constructor() {}

    private groupBy(xs, f) {
        return xs.reduce((r, v, i, a, k = f(v)) => ((r[k] || (r[k] = [])).push(v), r), {});
    }

    public groupLineItems = (items: []) => this.groupBy(items, li => li.productId);

    public getOrderId(items: LineItem[]): string | null {
        if (items[0] && items[0]?.order?.id) {
            return items[0].order.id;
        }

        return null;
    }

    // Simple calculation of subtotal for muliples
    // This value should be used for display and informational purposes only
    // Definitive currency calculations should occur on the backend
    public lineItemSubtotal = (price: string, num: number) => {
        let c = currency(price);
        let subtotal = c.multiply(num);
        return subtotal;
    };

    public calculateFeesApplied(order: Order) {
        let subtotal = currency(this.calculateSubtotal(order));
        let feePercentage = subtotal.multiply(order.settingsApplied.adminFeePercentage);
        let feeFixed = order.settingsApplied.adminFeeFixed;
        return feePercentage.add(feeFixed).toString();
    }

    public calculateTotal(order: Order) {
        let subtotal = currency(this.calculateSubtotal(order));

        return subtotal.add(this.calculateFeesApplied(order)).toString();
    }

    public calculateSubtotal(order: Order) {
        let subtotal = order.lineItems.reduce((acc, val) => acc.add(currency(val.price)), currency(0));
        return subtotal.toString();
    }

    /**
     * The GST already inside a total, not a surcharge on top of it.
     *
     * Every price in this system is GST-inclusive, so a $726 charge at 10% contains
     * $66 of GST - rate / (1 + rate) - not $72.60. Multiplying by the rate alone
     * overstated it by a tenth on every line that carries GST, which stayed invisible
     * while permits were the only line items and all of them are GST-free. A cultural
     * advisor line is the first that is not, and it made the CRM disagree with the
     * invoice for the same order.
     */
    public calculateGst(order: Order) {
        let subtotalWithGst = order.lineItems.reduce((acc, val) => acc.add(val.gstExempt ? currency(0) : currency(val.price)), currency(0));
        let totalWithGst = subtotalWithGst.add(this.calculateFeesApplied(order));
        let rate = currency(order.settingsApplied.gstPercentage);
        let gst = currency(totalWithGst).multiply(rate).divide(currency(1).add(rate));
        return gst.toString();
    }
}
